import React, { useEffect, useMemo, useState } from 'react';
import Table from 'components/Table';
import { Button, Form, Input } from 'antd';

import { SETTING_TYPES } from 'shared/constants/settings';

import { useQuery } from 'hooks/apollo';
import { gql } from '@apollo/client';
import useFormatMessage from 'hooks/useFormatMessage';
import { EditOutlined, StopOutlined } from '@ant-design/icons';
import { useSet } from 'ahooks';
import ArraySettingDrawer from './ArraySettingDrawer';
import SettingLabel, { settingLabel } from './SettingLabel';
import EditSettingValue from './EditSettingValue';
import SettingValue from './SettingValue';

const { ARRAY, SURVEY_GROUP_ID, PRICE } = SETTING_TYPES;

const QUERY = gql`
  query($relationId: ID, $keys: [String!]!, $globalKeys: [String!]!) {
    settings: settingsArray(relationId: $relationId, keys: $keys) {
      id
      key
      value
    }
    globalSettings: settingsArray(keys: $globalKeys) {
      id
      key
      value
    }
  }
`;

const columns = [
  {
    dataIndex: 'title',
    title: 'Setting',
    render: (_, record) => <SettingLabel setting={record.key} />,
  },
  {
    dataIndex: 'value',
    title: 'Value',
    render: (value, record) => (record.edit ? <EditSettingValue namePath={['settings']} settingKey={record.key} setting={record.setting} inputProps={record.inputProps} /> : <SettingValue setting={record.setting} value={value} />),
  },
];

const SettingsList = ({ settings, settingsValues, relationId, edit = false, hideEmpty = false, onFormSubmit, refresh }) => {
  const keys = useMemo(
    () => Object
      .keys(settings)
      .filter((key) => edit || !settings[key].private || settings[key].secret),
    [settings, edit],
  );
  const globalKeys = useMemo(() => [...new Set(keys.map(key => settings[key]?.globalSetting).filter(k => k))], [keys, settings]);
  const $t = useFormatMessage();
  const [search, setSearch] = useState();
  const { data, loading, refetch } = useQuery(QUERY, { variables: { relationId, keys, globalKeys }, skip: !keys.length || relationId === undefined || settingsValues != null });
  const [editingKeys, { add: addEditingKey, remove: removeEditingKey, reset: resetEditingKeys }] = useSet();
  const [editArraySetting, setEditArraySetting] = useState();
  const form = Form.useFormInstance();

  useEffect(() => {
    if (edit && relationId && keys.length) {
      resetEditingKeys();
    }
  }, [keys, relationId, edit]);

  useEffect(() => {
    if (refresh !== undefined) {
      refetch();
    }
  }, [refresh]);

  const dataSource = useMemo(() => keys.map(key => {
    const rawValue = settingsValues != null ? settingsValues[key] : data?.settings.find(s => s.key === key)?.value;
    const setting = settings[key];
    const value = rawValue ?? setting.defaultValue;
    const { type, globalSetting } = setting;

    const row = {
      setting,
      key,
      label: settingLabel($t, key),
      value,
      edit: editingKeys.has(key),
    };

    if (type === SURVEY_GROUP_ID) {
      row.inputProps = {
        customerId: data?.globalSettings?.find(k => k.key === globalSetting)?.value,
        selectables: ['group'],
      };
    }

    if (type === ARRAY || type === PRICE) {
      try {
        row.value = row.value ? JSON.parse(row.value) : null;
      } catch (e) {
        row.value = null;
      }
    }

    return row;
  }).filter(r => !hideEmpty || r.value != null), [data, keys, editingKeys, settings, $t]);

  const onEdit = (record) => {
    if (record.setting.type === ARRAY) {
      setEditArraySetting(record);
    } else {
      addEditingKey(record.key);
      form.setFieldValue(['settings', record.key], record.value);
    }
  };

  const onArraySettingsDone = () => {
    setEditArraySetting(null);
    refetch();
  };

  const actions = edit && ((record) => [
    editingKeys.has(record.key) ? (
      <Button danger key="cancelEdit" icon={<StopOutlined />} onClick={() => removeEditingKey(record.key)} />
    ) : (
      <Button key="edit" icon={<EditOutlined />} onClick={() => onEdit(record)} disabled={relationId === undefined && record.setting.type === ARRAY} />
    ),
  ]);

  return (
    <>
      <Input.Search onSearch={setSearch} allowClear placeholder="Search" style={{ marginBottom: '1em' }} />
      <Table
        loading={loading}
        bordered
        columns={columns}
        dataSource={dataSource}
        search={search}
        pagination={false}
        rowKey="key"
        actions={actions}
      />
      <ArraySettingDrawer
        arraySetting={editArraySetting}
        onCancel={() => setEditArraySetting(null)}
        onDone={onArraySettingsDone}
        onFormSubmit={onFormSubmit}
      />
    </>
  );
};

export default SettingsList;
